/**
 * RCA ticket filters for the Agents console (TRUS-687): the filter state, how it
 * is written to and read from the URL, the query it becomes, and defensive
 * parsing of the server's rollups, groups and saved views.
 *
 * Everything here is pure so it can be tested without mounting the console.
 *
 * Privacy: the URL carries ids and enum values only. Every value is checked
 * against a strict pattern on the way in AND out, so an email, a name or free
 * text can never land in the address bar. The search box (`q`) is not part of
 * this state at all: it lives in component state (see useRcaFilters).
 */

/** Every filter axis. Multi-value: OR within an axis, AND across axes. */
export const RCA_AXES = [
  'status',
  'verdict',
  'journey',
  'label',
  'priority',
  'due',
  'org',
  'origin',
  'reporter',
  'assignee',
  'issue_type',
  'jira_category',
  'has_pr',
  'reviewed_by',
  // TRUS-717: who the ticket is waiting on (derived from the hand-off stage, see waitingOn()).
  'waiting_on',
  // TRUS-717: exclusion axes — hide rows that match. `waiting_on_exclude=vineet` powers the
  // "hide waiting on Vineet" / "My review queue" ask. Enforced server-side so paged counts agree.
  'waiting_on_exclude',
  'reporter_exclude',
  'org_exclude',
] as const;
export type RcaAxis = (typeof RCA_AXES)[number];

/** Axes that hold at most one value (picking another replaces it). */
export const EXCLUSIVE_AXES: ReadonlySet<RcaAxis> = new Set<RcaAxis>(['has_pr', 'reviewed_by']);

export const GROUP_BY_OPTIONS = ['org', 'origin', 'verdict', 'status', 'journey', 'assignee', 'reporter', 'epic'] as const;
export type RcaGroupBy = (typeof GROUP_BY_OPTIONS)[number];
export const GROUP_BY_LABEL: Record<RcaGroupBy, string> = {
  org: 'Org',
  origin: 'Origin',
  verdict: 'Verdict',
  status: 'Status',
  journey: 'Journey',
  assignee: 'Assignee',
  reporter: 'Reporter',
  epic: 'Epic',
};
/** The filter a group's "Show more in this group" sets. Epic has no filter axis. */
export const GROUP_FILTER_AXIS: Partial<Record<RcaGroupBy, RcaAxis>> = {
  org: 'org',
  origin: 'origin',
  verdict: 'verdict',
  status: 'status',
  journey: 'journey',
  assignee: 'assignee',
  reporter: 'reporter',
};
/** Axes whose "no value" bucket is addressable as the literal `none`. */
const NONE_AXES: ReadonlySet<RcaAxis> = new Set<RcaAxis>(['org', 'origin', 'reporter', 'assignee', 'priority']);

export const ORIGIN_LABEL: Record<string, string> = {
  cs_widget: 'CS widget',
  jira_manual: 'Filed by a person',
  direct_session: 'Direct work · Claude Code',
  pipeline_split: 'Pipeline split',
  observability: 'Observability',
  pr_review: 'PR review',
  cs_ticket: 'CS ticket',
  none: 'Unknown',
};
export const ORIGIN_ORDER = Object.keys(ORIGIN_LABEL);
/** Short form for the row badge. */
export const ORIGIN_SHORT: Record<string, string> = {
  cs_widget: 'CS widget',
  jira_manual: 'Person',
  direct_session: 'Direct',
  pipeline_split: 'Split',
  observability: 'Observability',
  pr_review: 'PR review',
  cs_ticket: 'CS ticket',
};
export const JIRA_CATEGORY_LABEL: Record<string, string> = {
  new: 'To do',
  indeterminate: 'In progress',
  done: 'Done',
  none: 'Unknown',
};

// TRUS-717: "Waiting on" — who the ball is with on a ticket, from the hand-off stage rather
// than the Jira assignee (every ticket is assigned to Vineet in Jira, so assignee is useless).
export type WaitingOn = 'reviewer' | 'vineet' | 'agent' | 'creator';
export const WAITING_ON_ORDER: readonly WaitingOn[] = ['reviewer', 'vineet', 'agent', 'creator'];
export const WAITING_ON_LABEL: Record<WaitingOn, string> = {
  reviewer: 'Reviewer (Abby)',
  vineet: 'Vineet',
  agent: 'Agent',
  creator: 'Creator',
};

/** Columns the console can sort by. The server orders by the first group, the rest are window-only. */
export const SORT_KEYS = ['status', 'verdict', 'ticket', 'priority', 'due', 'source', 'updated', 'action', 'created', 'org', 'origin', 'assignee'] as const;
export type RcaSortKey = (typeof SORT_KEYS)[number];
/** Sorts every backend has always understood. */
export const LEGACY_SERVER_SORTS: ReadonlySet<string> = new Set(['updated', 'priority', 'due']);
/** Sorts the paged (TRUS-687) backend orders server-side. */
export const PAGED_SERVER_SORTS: ReadonlySet<string> = new Set(['created', 'updated', 'priority', 'due', 'status', 'org', 'origin', 'assignee']);

export interface RcaSort {
  key: RcaSortKey;
  dir: 'asc' | 'desc';
}

export interface RcaFilterState {
  axes: Record<RcaAxis, string[]>;
  sort: RcaSort;
  group: RcaGroupBy | '';
}

const emptyAxes = (): Record<RcaAxis, string[]> =>
  Object.fromEntries(RCA_AXES.map((a) => [a, [] as string[]])) as unknown as Record<RcaAxis, string[]>;

/** The console opens on the action queue, most urgent first, ungrouped. */
export function defaultRcaFilterState(): RcaFilterState {
  const axes = emptyAxes();
  axes.status = ['awaiting_review'];
  return { axes, sort: { key: 'priority', dir: 'asc' }, group: '' };
}

/** Everything cleared (the "Clear all" state): no axis filtered, sort and group kept. */
export function clearedAxes(): Record<RcaAxis, string[]> {
  return emptyAxes();
}

// ---- value validation -----------------------------------------------------------------

// Mirrors the server's per-axis token rules (agent_registry_router `_TOKEN_RE`): Jira
// priority and issue-type NAMES may contain spaces ("New Feature"); nothing allows '@'.
const TOKEN_RE: Partial<Record<RcaAxis, RegExp>> = {
  status: /^[a-z_]{1,32}$/,
  verdict: /^[a-z_]{1,40}$/,
  label: /^[A-Za-z0-9][A-Za-z0-9_.:-]{0,63}$/,
  priority: /^[A-Za-z][A-Za-z0-9 _-]{0,31}$/,
  issue_type: /^[A-Za-z][A-Za-z0-9 _-]{0,39}$/,
};
const TOKEN = /^[A-Za-z0-9][A-Za-z0-9_.-]{0,63}$/;
const ID_ME_NONE = /^(?:[1-9]\d{0,17}|me|none)$/;
const ID_OR_NONE = /^(?:[1-9]\d{0,17}|none)$/;
const ID_OR_ME = /^(?:[1-9]\d{0,17}|me)$/;
const ENUMS: Partial<Record<RcaAxis, ReadonlySet<string>>> = {
  journey: new Set(['intake', 'awaiting', 'needs_merge', 'building', 'held']),
  due: new Set(['overdue', 'due_soon', 'has_due', 'no_due']),
  origin: new Set(ORIGIN_ORDER),
  jira_category: new Set(Object.keys(JIRA_CATEGORY_LABEL)),
  has_pr: new Set(['true', 'false']),
  waiting_on: new Set(WAITING_ON_ORDER),
  waiting_on_exclude: new Set(WAITING_ON_ORDER),
};

/** True when `value` is an acceptable id/enum for `axis` (never an email or free text). */
export function isValidAxisValue(axis: RcaAxis, value: string): boolean {
  if (!value) return false;
  const e = ENUMS[axis];
  if (e) return e.has(value);
  if (axis === 'reporter' || axis === 'assignee' || axis === 'reporter_exclude') return ID_ME_NONE.test(value);
  if (axis === 'org' || axis === 'org_exclude') return ID_OR_NONE.test(value);
  if (axis === 'reviewed_by') return ID_OR_ME.test(value);
  return (TOKEN_RE[axis] ?? TOKEN).test(value);
}

function cleanValues(axis: RcaAxis, raw: unknown): string[] {
  const parts = Array.isArray(raw)
    ? raw.map((v) => String(v ?? ''))
    : typeof raw === 'string' || typeof raw === 'number' || typeof raw === 'boolean'
      ? String(raw).split(',')
      : [];
  const out: string[] = [];
  for (const p of parts) {
    const v = p.trim();
    if (isValidAxisValue(axis, v) && !out.includes(v)) out.push(v);
  }
  return EXCLUSIVE_AXES.has(axis) ? out.slice(0, 1) : out.slice(0, 50);
}

function cleanSort(key: unknown, dir: unknown, fallback: RcaSort): RcaSort {
  const k = String(key ?? '');
  if (!(SORT_KEYS as readonly string[]).includes(k)) return fallback;
  const d = dir === 'asc' || dir === 'desc' ? dir : k === 'updated' || k === 'created' ? 'desc' : 'asc';
  return { key: k as RcaSortKey, dir: d };
}

function cleanGroup(v: unknown): RcaGroupBy | '' {
  const s = String(v ?? '');
  return (GROUP_BY_OPTIONS as readonly string[]).includes(s) ? (s as RcaGroupBy) : '';
}

// ---- URL ------------------------------------------------------------------------------

/** URL keys this console owns (everything else in the query string is left alone). */
export const RCA_URL_KEYS: readonly string[] = [...RCA_AXES, 'sort', 'dir', 'group'];

/** Read the filter state from a query string. No filter keys at all → the default view. */
export function parseRcaUrl(search: string | URLSearchParams): RcaFilterState {
  const sp = typeof search === 'string' ? new URLSearchParams(search) : search;
  const def = defaultRcaFilterState();
  const owned = RCA_URL_KEYS.some((k) => sp.has(k));
  if (!owned) return def;
  const axes = emptyAxes();
  for (const a of RCA_AXES) {
    const raw = sp.get(a);
    if (raw == null) {
      // An absent axis means "not filtered" once the URL carries any state — except
      // status, whose absence keeps the default queue (only `status=all` clears it).
      if (a === 'status') axes.status = [...def.axes.status];
      continue;
    }
    axes[a] = a === 'status' && raw === 'all' ? [] : cleanValues(a, raw);
  }
  return { axes, sort: cleanSort(sp.get('sort'), sp.get('dir'), def.sort), group: cleanGroup(sp.get('group')) };
}

/**
 * Write the filter state into `base` (other params, e.g. an unrelated tab's, are kept).
 * Only what differs from the default is written, so the default view is a bare URL.
 */
export function writeRcaUrl(state: RcaFilterState, base: string | URLSearchParams = ''): URLSearchParams {
  const sp = new URLSearchParams(typeof base === 'string' ? base : base.toString());
  for (const k of RCA_URL_KEYS) sp.delete(k);
  const def = defaultRcaFilterState();
  for (const a of RCA_AXES) {
    const vals = cleanValues(a, state.axes[a] ?? []);
    if (a === 'status') {
      if (sameValues(vals, def.axes.status)) continue;
      sp.set('status', vals.length ? vals.join(',') : 'all');
      continue;
    }
    if (vals.length) sp.set(a, vals.join(','));
  }
  if (state.sort.key !== def.sort.key || state.sort.dir !== def.sort.dir) {
    sp.set('sort', state.sort.key);
    sp.set('dir', state.sort.dir);
  }
  if (state.group) sp.set('group', state.group);
  return sp;
}

export function sameValues(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const s = new Set(a);
  return b.every((v) => s.has(v));
}

export function sameFilterState(a: RcaFilterState, b: RcaFilterState): boolean {
  return (
    RCA_AXES.every((ax) => sameValues(a.axes[ax], b.axes[ax])) &&
    a.sort.key === b.sort.key &&
    a.sort.dir === b.sort.dir &&
    a.group === b.group
  );
}

// ---- API query ------------------------------------------------------------------------

/** The `/agents/rca/log` params for a filter state (cursor/limit/sort are added by the caller). */
export type RcaAxisParams = Partial<Record<RcaAxis, string>>;

export function axisParams(state: RcaFilterState): RcaAxisParams {
  const out: RcaAxisParams = {};
  for (const a of RCA_AXES) {
    const vals = cleanValues(a, state.axes[a] ?? []);
    if (vals.length) out[a] = vals.join(',');
  }
  return out;
}

export function activeFilterCount(state: RcaFilterState): number {
  return RCA_AXES.reduce((n, a) => n + (state.axes[a]?.length ?? 0), 0);
}

/** The server's ORDER BY for a column, given which backend we're talking to. */
export function serverSortFor(key: string, paged: boolean): string {
  const set = paged ? PAGED_SERVER_SORTS : LEGACY_SERVER_SORTS;
  return set.has(key) ? key : 'updated';
}

// ---- saved views ----------------------------------------------------------------------

export interface RcaViewParams extends RcaAxisParams {
  q?: string;
  sort?: string;
  group_by?: string;
}
export interface RcaSavedView {
  id: string;
  name: string;
  params: RcaViewParams;
  builtin: boolean;
}

/** Shown when the server sends no built-ins of its own (same set the contract defines). */
export const FALLBACK_BUILTIN_VIEWS: RcaSavedView[] = [
  { id: 'builtin:my-tickets', name: 'My tickets', params: { assignee: 'me' }, builtin: true },
  { id: 'builtin:from-cs', name: 'From CS', params: { origin: 'cs_widget,cs_ticket' }, builtin: true },
  { id: 'builtin:direct-work', name: 'Direct work', params: { origin: 'direct_session' }, builtin: true },
  { id: 'builtin:pipeline-created', name: 'Pipeline-created', params: { origin: 'pipeline_split' }, builtin: true },
  { id: 'builtin:needs-merge', name: 'Needs merge', params: { journey: 'needs_merge' }, builtin: true },
  { id: 'builtin:no-org', name: 'No org', params: { org: 'none' }, builtin: true },
  // TRUS-717: everything that still needs reviewer/agent attention — i.e. not handed to Vineet.
  // No status key, so it spans every status (an absent status reads as "all").
  { id: 'builtin:my-review-queue', name: 'My review queue', params: { waiting_on_exclude: 'vineet' }, builtin: true },
];

export const MAX_Q_LENGTH = 200;

/**
 * A view's params for the current state, in the form the server's validator stores
 * (`_validate_view_params`): comma-joined ids/enums per axis, q ≤ 200, a server sort key,
 * group_by. An axis left out means "not filtered" (status included), so nothing else is sent.
 */
export function viewParamsFrom(state: RcaFilterState, q: string): RcaViewParams {
  const p: RcaViewParams = { ...axisParams(state) };
  const trimmed = q.trim().slice(0, MAX_Q_LENGTH);
  if (trimmed) p.q = trimmed;
  // Window-only columns (ticket, verdict, …) aren't server sorts: the validator would 400.
  if (PAGED_SERVER_SORTS.has(state.sort.key)) p.sort = state.sort.key;
  if (state.group) p.group_by = state.group;
  return p;
}

/** Apply a view's params: a full state (axes not named are cleared) plus its q. */
export function stateFromViewParams(params: unknown, current: RcaFilterState): { state: RcaFilterState; q: string } {
  const p = (params && typeof params === 'object' ? params : {}) as Record<string, unknown>;
  const axes = emptyAxes();
  for (const a of RCA_AXES) {
    if (a === 'status' && (p.status === 'all' || p.status === undefined)) continue;
    axes[a] = cleanValues(a, p[a]);
  }
  // A view names a sort column, not a direction: keep the current direction on the same column.
  const dir = p.dir ?? (p.sort === current.sort.key ? current.sort.dir : undefined);
  const sort = p.sort !== undefined ? cleanSort(p.sort, dir, current.sort) : current.sort;
  const q = typeof p.q === 'string' ? p.q.slice(0, MAX_Q_LENGTH) : '';
  return { state: { axes, sort, group: cleanGroup(p.group_by ?? p.group) }, q };
}

export function parseRcaViews(res: unknown): RcaSavedView[] | null {
  const d = (res as any)?.data ?? res;
  if ((res as any)?.success === false) return null;
  const list = Array.isArray(d) ? d : Array.isArray(d?.items) ? d.items : Array.isArray(d?.views) ? d.views : null;
  if (!list) return null;
  const out: RcaSavedView[] = [];
  for (const v of list) {
    if (!v || typeof v !== 'object') continue;
    const id = v.id != null ? String(v.id) : '';
    const name = typeof v.name === 'string' ? v.name : '';
    if (!id || !name) continue;
    const builtin = v.builtin === true || id.startsWith('builtin:');
    let params = v.params;
    if (typeof params === 'string') {
      try {
        params = JSON.parse(params);
      } catch {
        params = {};
      }
    }
    out.push({ id, name, params: params && typeof params === 'object' ? params : {}, builtin });
  }
  return out;
}

// ---- rollups --------------------------------------------------------------------------

export interface RollupEntry {
  id: string; // 'none' for the null bucket
  name: string;
  count: number;
}

/** A value→count map from either `{k: n}` or `[{key|value|name, count}]`. */
export function parseCountMap(v: unknown, keyFields: string[] = ['key', 'value', 'name']): Record<string, number> {
  const out: Record<string, number> = {};
  if (Array.isArray(v)) {
    for (const e of v) {
      if (!e || typeof e !== 'object') continue;
      let k: unknown;
      for (const f of keyFields) {
        if (f in e) {
          k = (e as any)[f];
          break;
        }
      }
      const key = k == null || k === '' ? 'none' : String(k);
      out[key] = (out[key] || 0) + (Number((e as any).count) || 0);
    }
  } else if (v && typeof v === 'object') {
    for (const [k, n] of Object.entries(v as Record<string, unknown>)) {
      const key = k === '' || k === 'null' ? 'none' : k;
      out[key] = (out[key] || 0) + (Number(n) || 0);
    }
  }
  return out;
}

/** `[{cbo_id|user_id|id, name, count}]` → entries with a string id (`none` for null). */
export function parseEntityCounts(v: unknown, idField: string): RollupEntry[] {
  const out: RollupEntry[] = [];
  if (Array.isArray(v)) {
    for (const e of v) {
      if (!e || typeof e !== 'object') continue;
      const raw = (e as any)[idField] ?? (e as any).id ?? null;
      const id = raw == null || raw === '' ? 'none' : String(raw);
      const count = Number((e as any).count) || 0;
      const name = typeof (e as any).name === 'string' && (e as any).name ? (e as any).name : '';
      out.push({ id, name, count });
    }
  } else if (v && typeof v === 'object') {
    for (const [k, n] of Object.entries(v as Record<string, unknown>)) {
      out.push({ id: k === '' || k === 'null' ? 'none' : k, name: '', count: Number(n) || 0 });
    }
  }
  return out;
}

export interface RcaRollups {
  org: RollupEntry[] | null;
  origin: Record<string, number> | null;
  reporter: RollupEntry[] | null;
  assignee: RollupEntry[] | null;
  issue_type: Record<string, number> | null;
  jira_category: Record<string, number> | null;
  has_pr: Record<string, number> | null;
}

/** The TRUS-687 rollups from a log envelope; each is null when the server didn't send it. */
export function parseRcaRollups(data: any): RcaRollups {
  const pick = (k: string) => data?.[k] ?? data?.data?.[k];
  const has = (k: string) => pick(k) != null;
  return {
    org: has('org_counts') ? parseEntityCounts(pick('org_counts'), 'cbo_id') : null,
    origin: has('origin_counts') ? parseCountMap(pick('origin_counts'), ['origin', 'key', 'value']) : null,
    reporter: has('reporter_counts') ? parseEntityCounts(pick('reporter_counts'), 'user_id') : null,
    assignee: has('assignee_counts') ? parseEntityCounts(pick('assignee_counts'), 'user_id') : null,
    issue_type: has('issue_type_counts') ? parseCountMap(pick('issue_type_counts'), ['issue_type', 'key', 'value', 'name']) : null,
    jira_category: has('jira_category_counts') ? parseCountMap(pick('jira_category_counts'), ['jira_category', 'category', 'key', 'value']) : null,
    has_pr: has('has_pr_counts') ? parseCountMap(pick('has_pr_counts'), ['has_pr', 'key', 'value']) : null,
  };
}

export const EMPTY_ROLLUPS: RcaRollups = {
  org: null,
  origin: null,
  reporter: null,
  assignee: null,
  issue_type: null,
  jira_category: null,
  has_pr: null,
};

// ---- groups ---------------------------------------------------------------------------

export interface RcaGroup<T = any> {
  key: string; // 'none' for the null bucket
  label: string;
  count: number;
  items: T[];
}

export function parseRcaGroups<T = any>(data: any): RcaGroup<T>[] | null {
  const g = data?.groups ?? data?.data?.groups;
  if (!Array.isArray(g)) return null;
  return g
    .filter((x: any) => x && typeof x === 'object')
    .map((x: any) => {
      const items = Array.isArray(x.items) ? x.items : [];
      const key = x.key == null || x.key === '' ? 'none' : String(x.key);
      const label = typeof x.label === 'string' && x.label ? x.label : key === 'none' ? 'None' : key;
      return { key, label, count: Number(x.count) || items.length, items };
    });
}

/** "Show more in this group": the filter to set, or null when the axis has no filter. */
export function groupFilterFor(group: RcaGroupBy, key: string): { axis: RcaAxis; value: string } | null {
  const axis = GROUP_FILTER_AXIS[group];
  if (!axis) return null;
  if (key === 'none' && !NONE_AXES.has(axis)) return null;
  return isValidAxisValue(axis, key) ? { axis, value: key } : null;
}

// ---- people on a row ------------------------------------------------------------------

export interface RcaPerson {
  user_id?: number | string | null;
  name?: string | null;
  email?: string | null;
}

export function personLabel(p: RcaPerson | null | undefined): string {
  if (!p || typeof p !== 'object') return '';
  return (p.name || p.email || (p.user_id != null ? `user ${p.user_id}` : '') || '').trim();
}

/**
 * Who a ticket is waiting on (TRUS-717), derived from the hand-off stage — never the Jira
 * assignee, which is always Vineet. This MUST mirror lifecycle() in AgentControlPage (same
 * inputs → the same audience the status pill implies), so the row's "Waiting on" field and
 * its status pill can never disagree; a table test pins both to the same fixtures.
 *
 * Returns null for a settled ticket (done / skipped / failed) — nobody is actively waited on.
 * The server's _WAITING_ON_SQL mirrors this exactly so paged lists and counts match.
 */
export function waitingOn(t: {
  status?: string | null;
  verdict?: string | null;
  held_for?: string | null;
}): WaitingOn | null {
  const status = (t.status || '').toLowerCase();
  // OPERATIONAL / CONFIGURATION are the hand-off (no-code) verdicts: an approved one goes back
  // to the agent to execute, not to Vineet to merge (cf. isHandoffVerdict + lifecycle()).
  const operational = ['operational', 'configuration'].includes((t.verdict || '').toLowerCase());
  switch (status) {
    case 'reviewed':
      // Approved: an operational change is the agent's to complete ("Next: complete"); a code
      // change is handed to Vineet to merge/deploy ("Next: merge" / "Approved for implementation").
      return operational ? 'agent' : 'vineet';
    case 'pending':
    case 'claimed':
      // Queued / Building / Re-analyzing / In progress — the agent's court.
      return 'agent';
    case 'awaiting_review':
      // Awaiting review / Next: code review / operational "Needs action" — the reviewer's.
      return 'reviewer';
    case 'product_approved':
      // Product-approved — ready to build: handed to Vineet for a separate build session.
      return 'vineet';
    case 'held': {
      // intake / product / admin / creator_draft are all reviewer-side gates; only a plain
      // creator hold (or the legacy NULL held_for) is actually waiting on the reporter.
      const audience = (t.held_for || '').toLowerCase();
      return ['intake', 'product', 'admin', 'creator_draft'].includes(audience) ? 'reviewer' : 'creator';
    }
    default:
      return null;
  }
}
